import WarningIcon from "@mui/icons-material/Warning";
import { Box } from "@mui/material";
import Button from "@mui/material/Button";
import MuiDialog, { type DialogProps } from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Typography from "@mui/material/Typography";
import type { ReactNode } from "react";

type Props = {
    open: boolean;
    onPrimaryButtonClick?: () => void;
    onClose: () => void;
    title: string;
    primaryButtonText?: string;
    secondaryButtonText?: string;
    showPrimaryButton?: boolean;
    showSecondaryButton?: boolean;
    children: ReactNode;
    centerActionButtons?: boolean;
    showWarningIcon?: boolean;
    isLoading?: boolean;
    disablePrimaryButton?: boolean;
    // Forwarded to the MUI Dialog. Unset = MUI's default shrink-to-content `sm` paper, whose width
    // follows the widest child — pass both to pin the width when the content changes while open.
    fullWidth?: boolean;
    maxWidth?: DialogProps["maxWidth"];
};

const ConfirmDialog = ({
    open,
    onPrimaryButtonClick,
    onClose,
    title,
    primaryButtonText = "Ok",
    secondaryButtonText = "Cancel",
    children,
    showPrimaryButton = true,
    showSecondaryButton = true,
    centerActionButtons = false,
    showWarningIcon = true,
    isLoading = false,
    disablePrimaryButton = false,
    fullWidth,
    maxWidth,
}: Props) => {
    return (
        <MuiDialog
            open={open}
            onClose={onClose}
            fullWidth={fullWidth}
            maxWidth={maxWidth}
            aria-labelledby="confirm-alert-dialog-title"
            aria-describedby="confirm-alert-dialog-description"
        >
            <DialogTitle id="confirm-alert-dialog-title">
                <Box display={"flex"} alignItems="center" gap={1}>
                    {showWarningIcon && <WarningIcon color="error" />}
                    {title}
                </Box>
            </DialogTitle>
            <DialogContent id="confirm-alert-dialog-description">
                <Typography variant="body1" component={"div"}>
                    {children}
                </Typography>
            </DialogContent>
            <DialogActions
                sx={{
                    paddingBottom: "1rem",
                    paddingRight: "1.5rem",
                    justifyContent: centerActionButtons ? "center" : "flex-end",
                }}
            >
                {showPrimaryButton && onPrimaryButtonClick && (
                    <Button
                        onClick={onPrimaryButtonClick}
                        variant="contained"
                        loading={isLoading}
                        disabled={disablePrimaryButton}
                    >
                        {primaryButtonText}
                    </Button>
                )}

                {showSecondaryButton && (
                    <Button onClick={onClose} color="secondary">
                        {secondaryButtonText}
                    </Button>
                )}
            </DialogActions>
        </MuiDialog>
    );
};

export default ConfirmDialog;
